import React, { memo } from "react";
import { Control, FieldErrors, FieldArrayWithId } from "react-hook-form";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { CustomInput } from "../CustomInput";
import {
  AttendeeFormData,
  AdditionalAttendee,
} from "../../../hooks/useEventCardState";

const DARK_INPUT_CLASS =
  "bg-white/10 text-white border-white/30 placeholder-white/40 " +
  "focus:ring-white/50 focus:border-transparent rounded-lg";

interface AttendeeFormProps {
  fields: FieldArrayWithId<AttendeeFormData, "attendees", "id">[];
  control: Control<AttendeeFormData>;
  errors: FieldErrors<AttendeeFormData>;
  watchedAttendees: AdditionalAttendee[];
  expandedAttendees: Set<number>;
  onToggleExpanded: (index: number) => void;
  isProcessing: boolean;
  isCalculated: boolean;
  onAttendeeEmailChange: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const AttendeeForm: React.FC<AttendeeFormProps> = memo(
  ({
    fields,
    control,
    errors,
    watchedAttendees,
    expandedAttendees,
    onToggleExpanded,
    isProcessing,
    isCalculated,
    onAttendeeEmailChange,
    t,
  }) => {
    if (fields.length === 0) return null;

    return (
      <div>
        <label className="block text-sm font-medium text-white mb-3">
          {t("event.purchase.additionalAttendees", {
            count: fields.length,
          })}
        </label>
        {fields.map((field, index) => {
          const isExpanded = expandedAttendees.has(index);
          const attendeeData = watchedAttendees[index] || {
            first_name: "",
            last_name: "",
            user_email: "",
          };

          return (
            <div
              key={field.id}
              className="mb-4 border border-white/20 rounded-xl bg-black/20 backdrop-blur overflow-hidden"
            >
              <button
                type="button"
                onClick={() => onToggleExpanded(index)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-sm font-medium text-white truncate">
                    {attendeeData.first_name
                      ? attendeeData.first_name
                      : t("event.purchase.attendee", { number: index + 2 })}
                  </span>
                </div>
                {isExpanded ? (
                  <HiChevronUp className="w-5 h-5 text-white/70" />
                ) : (
                  <HiChevronDown className="w-5 h-5 text-white/70" />
                )}
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-3">
                  <CustomInput
                    name={`attendees.${index}.first_name`}
                    control={control}
                    label={`${t("event.purchase.firstName")} *`}
                    labelClassName="text-white/70"
                    placeholder={
                      t("event.purchase.firstNamePlaceholder") as string
                    }
                    error={errors.attendees?.[index]?.first_name}
                    helperText={errors.attendees?.[index]?.first_name}
                    maxLength={30}
                    disabled={isProcessing}
                    inputClassName={DARK_INPUT_CLASS}
                  />
                  <CustomInput
                    name={`attendees.${index}.last_name`}
                    control={control}
                    label={`${t("event.purchase.lastName")} *`}
                    labelClassName="text-white/70"
                    placeholder={
                      t("event.purchase.lastNamePlaceholder") as string
                    }
                    error={errors.attendees?.[index]?.last_name}
                    helperText={errors.attendees?.[index]?.last_name}
                    maxLength={30}
                    disabled={isProcessing}
                    inputClassName={DARK_INPUT_CLASS}
                  />
                  <CustomInput
                    name={`attendees.${index}.user_email`}
                    control={control}
                    type="email"
                    inputDir="ltr"
                    label={`${t("key21")} *`}
                    labelClassName="text-white/70"
                    placeholder={t("event.purchase.emailPlaceholder") as string}
                    error={errors.attendees?.[index]?.user_email}
                    helperText={errors.attendees?.[index]?.user_email}
                    maxLength={200}
                    disabled={isProcessing}
                    onValueChange={() => {
                      if (isCalculated) {
                        onAttendeeEmailChange();
                      }
                    }}
                    inputClassName={DARK_INPUT_CLASS}
                  />
                </div>
              )}
            </div>
          );
        })}
        <div className="bg-amber-500/20 border-2 border-amber-400/40 rounded-xl p-3 backdrop-blur">
          <p className="text-xs font-bold text-amber-200 mb-1">
            {t("event.purchase.validationWarning")}
          </p>
          <p className="text-xs text-amber-100">
            {t("event.purchase.validationMessage")}
          </p>
        </div>
      </div>
    );
  }
);

AttendeeForm.displayName = "AttendeeForm";

export default AttendeeForm;
